import React from 'react';

interface Props {
  label: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

// Isolates WebGL / canvas rendering crashes so they don't unmount the whole editor.
// Selection, panels, and undo history survive a reset. A manual "Try again"
// remounts the child tree.
export class CanvasErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(`[${this.props.label}] render crashed:`, error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-secondary p-6">
          <div className="max-w-md text-center">
            <div className="text-base font-semibold mb-2">
              {this.props.label} stopped responding
            </div>
            <div className="text-sm text-muted-foreground mb-4 break-words">
              {this.state.error.message || 'An unexpected rendering error occurred.'}
            </div>
            <button
              onClick={this.reset}
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm hover:opacity-90"
            >
              Try again
            </button>
            <div className="text-xs text-muted-foreground mt-3">
              Your stage data is safe — use Undo (Ctrl+Z) if the last action caused this.
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default CanvasErrorBoundary;
